import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableFooter,
  TableRow,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import ReportDataService from '../../../services/report.service';
import { useTranslation } from '../../../hooks/useTranslation';

const fmtHours = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '0';
  return String(Math.round(n * 100) / 100);
};

const CleaningHoursByClientReport = () => {
  const { t, language } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [startDate, setStartDate] = useState(dayjs().subtract(30, 'day'));
  const [endDate, setEndDate] = useState(dayjs());
  const [tab, setTab] = useState(0);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {};
      if (startDate) params.start_date = startDate.format('YYYY-MM-DD');
      if (endDate) params.end_date = endDate.format('YYYY-MM-DD');
      const res = await ReportDataService.getCleaningHoursByClient(params);
      setData(res.data?.data || null);
    } catch (e) {
      setError(e.response?.data?.message || t('reports.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = data?.rows || [];
  const summary = data?.summary || {};
  const locale = language === 'es' ? 'es-ES' : 'en-US';

  const formattedRows = useMemo(() => {
    return rows.map((r) => ({
      ...r,
      _dateLabel: r.service_date
        ? new Date(
            String(r.service_date).includes('T') ? r.service_date : `${String(r.service_date).slice(0, 10)}T12:00:00`
          ).toLocaleDateString(locale, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })
        : '',
    }));
  }, [rows, locale]);

  const byClientAggregates = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      const id = r.client_id;
      if (id == null) return;
      const cur = m.get(id) || {
        client_id: id,
        client_name: r.client_name || '',
        total_hours: 0,
      };
      cur.total_hours += Number(r.total_hours) || 0;
      m.set(id, cur);
    });
    return Array.from(m.values()).sort((a, b) => String(a.client_name || '').localeCompare(String(b.client_name || '')));
  }, [rows]);

  const hasRows = formattedRows.length > 0;

  return (
    <Box>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
        {t('reports.cleaningHoursByClient')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('reports.cleaningHoursByClientDescription')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
              <DatePicker label={t('reports.startDate')} value={startDate} onChange={(v) => setStartDate(v)} />
              <DatePicker label={t('reports.endDate')} value={endDate} onChange={(v) => setEndDate(v)} />
              <Button variant="contained" onClick={load} disabled={loading}>
                {t('reports.filter')}
              </Button>
            </Box>
          </LocalizationProvider>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tab label={t('reports.cleaningTabDetail')} />
                <Tab label={t('reports.cleaningTabByClient')} />
              </Tabs>

              {tab === 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('reports.date')}</TableCell>
                        <TableCell>{t('reports.cleaningClient')}</TableCell>
                        <TableCell align="right">{t('reports.cleaningHours')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!hasRows ? (
                        <TableRow>
                          <TableCell colSpan={3}>{t('reports.noData')}</TableCell>
                        </TableRow>
                      ) : (
                        formattedRows.map((r) => (
                          <TableRow key={`${r.client_id}-${r.service_date}`}>
                            <TableCell>{r._dateLabel}</TableCell>
                            <TableCell>{r.client_name}</TableCell>
                            <TableCell align="right">{fmtHours(r.total_hours)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                    {hasRows && (
                      <TableFooter>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell colSpan={2} sx={{ fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}>
                            {t('common.total')}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}
                          >
                            {fmtHours(summary.total_hours)}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </TableContainer>
              )}

              {tab === 1 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('reports.cleaningClient')}</TableCell>
                        <TableCell align="right">{t('reports.cleaningHours')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {!hasRows ? (
                        <TableRow>
                          <TableCell colSpan={2}>{t('reports.noData')}</TableCell>
                        </TableRow>
                      ) : (
                        byClientAggregates.map((r) => (
                          <TableRow key={r.client_id}>
                            <TableCell>{r.client_name}</TableCell>
                            <TableCell align="right">{fmtHours(r.total_hours)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                    {hasRows && (
                      <TableFooter>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell sx={{ fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}>
                            {t('common.total')}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' }}
                          >
                            {fmtHours(summary.total_hours)}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    )}
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CleaningHoursByClientReport;
